"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import type { ReactNode } from "react";
import PackageCard from "@/app/(main)/components/PackageCard";
import CruiseRegionsWidget from "@/app/(main)/cruises/components/CruiseRegionsWidget";
import type { HomePackage } from "@/lib/navigation";

// ─── API Client Wrapper ──────────────────────────────────────────────

import {
  fetchCruisesClient,
  type CruisePackageItem,
  type CruiseRegionMenu,
} from "@/lib/cruises";

// ─── Map CruisePackageItem → HomePackage ─────────────────────────────

function toHomePackage(pack: CruisePackageItem): HomePackage {
  const cities =
    pack.packageRegions
      ?.flatMap((r) => r.cityDays ?? [])
      .map((c) => c.cityName)
      .filter(Boolean) ?? [];

  return {
    id: pack.id,
    pageURL: pack.pageURL,
    packageName: pack.packageName,
    photoURL: pack.photoGallery?.[0]?.url || "/main/img/tour/tour1.jpg",
    startingPrice: Number(pack.rate?.startingFrom ?? 0),
    cities,
    days: pack.days ?? 0,
    nights: pack.nights ?? 0,
    cruiseNights: pack.cruiseNights ?? 0,
    packageTypeName:
      pack.packageTypeName ?? (pack.days > 0 ? `${pack.days} Days` : ""),
  };
}

// ─── Skeleton Loader ─────────────────────────────────────────────────

function PackageCardSkeleton() {
  return (
    <div className="col-md-6">
      <div
        className="tour-box th-ani h-100"
        style={{ overflow: "hidden", borderRadius: 8 }}
      >
        <div
          className="skeleton-pulse"
          style={{ height: 250, background: "#e9ecef" }}
        />
        <div className="tour-content" style={{ padding: "16px" }}>
          <div
            className="skeleton-pulse mb-2"
            style={{
              height: 14,
              width: "40%",
              background: "#e9ecef",
              borderRadius: 4,
            }}
          />
          <div
            className="skeleton-pulse mb-3"
            style={{
              height: 20,
              width: "85%",
              background: "#e9ecef",
              borderRadius: 4,
            }}
          />
          <div
            className="skeleton-pulse mb-2"
            style={{
              height: 12,
              width: "60%",
              background: "#e9ecef",
              borderRadius: 4,
            }}
          />
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div
              className="skeleton-pulse"
              style={{
                height: 18,
                width: "30%",
                background: "#e9ecef",
                borderRadius: 4,
              }}
            />
            <div
              className="skeleton-pulse"
              style={{
                height: 36,
                width: 90,
                background: "#e9ecef",
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pagination Arrows ───────────────────────────────────────────────

function PaginationArrows({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  loading,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1 && totalCount <= 0) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="d-flex align-items-center justify-content-between mb-4">
      <p className="blog-text mb-0">
        Showing{" "}
        <strong>
          {startItem}–{endItem}
        </strong>{" "}
        of <strong>{totalCount}</strong> packages
      </p>
      {totalPages > 1 && (
        <div className="d-flex gap-2">
          <button
            className="th-btn style4"
            disabled={currentPage <= 1 || loading}
            onClick={() => onPageChange(currentPage - 1)}
            style={{ padding: "4px 10px" }}
          >
            <i className="fa fa-arrow-left"></i> Prev
          </button>
          <button
            className="th-btn style4"
            disabled={currentPage >= totalPages || loading}
            onClick={() => onPageChange(currentPage + 1)}
            style={{ padding: "4px 10px" }}
          >
            Next <i className="fa fa-arrow-right"></i>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Client Component ───────────────────────────────────────────

export default function CruisesListClient({
  initialPackages,
  initialTotalPages,
  initialTotalCount,
  cruiseRegions,
  sidebarExtra,
}: {
  initialPackages: CruisePackageItem[];
  initialTotalPages: number;
  initialTotalCount: number;
  cruiseRegions: CruiseRegionMenu[];
  sidebarExtra?: ReactNode;
}) {
  const PAGE_SIZE = 10;
  const [packages, setPackages] = useState(initialPackages);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const shouldScrollRef = useRef(false);

  const fetchPackages = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const data = await fetchCruisesClient(page, PAGE_SIZE);
      setPackages(data.cruises);
      setTotalPages(data.totalPages);
      setTotalCount(data.count);
    } catch (err) {
      console.error("Failed to fetch packages:", err);
    } finally {
      setIsLoading(false);
      if (shouldScrollRef.current) {
        shouldScrollRef.current = false;
        requestAnimationFrame(() => scrollToGrid());
      }
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchPackages(currentPage);
  }, [currentPage, fetchPackages]);

  function scrollToGrid() {
    if (gridRef.current) {
      const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }

  function handlePageChange(page: number) {
    shouldScrollRef.current = true;
    setCurrentPage(page);
  }

  // Build pagination entries with stable keys
  const paginationEntries: {
    key: string;
    type: "page" | "ellipsis";
    value: number;
  }[] = [];
  if (totalPages > 1) {
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
    } else {
      paginationEntries.push({ key: "p1", type: "page", value: 1 });
      if (currentPage > 3) {
        paginationEntries.push({
          key: "el-start",
          type: "ellipsis",
          value: 0,
        });
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
      if (currentPage < totalPages - 2) {
        paginationEntries.push({
          key: "el-end",
          type: "ellipsis",
          value: 0,
        });
      }
      paginationEntries.push({
        key: `p${totalPages}`,
        type: "page",
        value: totalPages,
      });
    }
  }

  const sortedPackages = [...packages].sort(
    (a, b) => (a.websiteOrder ?? 999) - (b.websiteOrder ?? 999),
  );

  return (
    <div className="row g-3" ref={gridRef}>
      {/* ── Left: Package Cards ── */}
      <div className="col-xxl-8 col-lg-7">
        <div className="page-single">

          {/* Top pagination arrows */}
          {!isLoading && totalCount > 0 && (
            <PaginationArrows
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              loading={isLoading}
              onPageChange={handlePageChange}
            />
          )}

          <div
            className="row gy-4 gx-4"
            style={{
              opacity: isLoading ? 0.3 : 1,
              transition: "opacity 0.3s ease-in-out",
              minHeight: 200,
            }}
          >
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <PackageCardSkeleton key={i} />
              ))
            ) : sortedPackages.length > 0 ? (
              sortedPackages.map((pack) => (
                <div key={pack.id} className="col-md-6">
                  <PackageCard pack={toHomePackage(pack)} />
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <div className="mb-4">
                  <i className="fa-light fa-ship fa-4x text-theme"></i>
                </div>
                <p className="blog-text mb-4">No cruise packages found.</p>
              </div>
            )}
          </div>

          {/* Bottom pagination */}
          <div
            className="mc-pagination"
            style={{ display: totalPages > 1 ? "flex" : "none" }}
          >
            <button
              className="mc-pagination-btn mc-pagination-prev"
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className="fa fa-chevron-left"></i>
            </button>

            {paginationEntries.map((entry) =>
              entry.type === "ellipsis" ? (
                <span key={entry.key} className="mc-pagination-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={entry.key}
                  className={`mc-pagination-btn ${currentPage === entry.value ? "mc-pagination-active" : ""}`}
                  onClick={() => handlePageChange(entry.value)}
                  disabled={isLoading}
                >
                  {entry.value}
                </button>
              ),
            )}

            <button
              className="mc-pagination-btn mc-pagination-next"
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* ── Right: Sidebar ── */}
      <div className="col-xxl-4 col-lg-5">
        <aside
          className="sidebar-area style3 position-sticky"
          style={{ top: 100 }}
        >
          {/* Cruise regions → destinations (same as the header Cruises menu) */}
          <CruiseRegionsWidget regions={cruiseRegions} />

          {/* Sidebar extra */}
          {sidebarExtra}
        </aside>
      </div>

      {/* ── Skeleton pulse animation ── */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes skeletonPulse {
              0% { opacity: 0.6; }
              50% { opacity: 1; }
              100% { opacity: 0.6; }
            }
            .skeleton-pulse {
              animation: skeletonPulse 1.5s ease-in-out infinite;
            }
          `,
        }}
      />
    </div>
  );
}
